import React, { useRef, useMemo, useState, useEffect } from 'react'
import loadable from '@loadable/component'
import { useLocation } from 'react-router-dom'
import useSessionData from 'app/utils/useSessionData'
import { useSessionRecording } from 'lib/posthog'
import { posthog } from 'posthog-js'

const EXPERIMENTS = {
  LEAD_FORM_LOCO_VENDING: 'loco_lead_form_vending',
}

const EXPERIMENT_VARIANTS = {
  TEST: 'test',
  CONTROL: 'control',
}

const shownVariant = function(featureFlagValue, queryParamValue) {
  if (queryParamValue === 'true') {
    return EXPERIMENT_VARIANTS.TEST
  }

  return featureFlagValue || EXPERIMENT_VARIANTS.CONTROL
}

const LeadFormModal = loadable(() => import('./LeadFormModal'), {
  ssr: false
})

export default function LeadModal(props) {
  const ref = useRef()
  const location = useLocation()
  const [sessionData, setSessionData] = useSessionData()
  const [experiments, setExperiments] = useState({})
  const recording = useSessionRecording({ auto: false })
  const initialData = props.initialData || {}

  const getInitialData = () => ({
    foods: [],
    meals: [],
    contact: {
      ...sessionData.contact,
      sms_opt_in: false
    },
    address: {
      country_code: 'US',
    },
    ...initialData,
    meta: {
      ...initialData.meta,
      posthog_experiments: {
        ...initialData.meta?.posthog_experiments,
        ...experiments
      }
    },
    location_status: 'approximate',
    timeframe: 'asap',
  })

  const [data, setData] = useState(() => {
    return getInitialData()
  })

  // Allow the user to manually turn on the experiment using a query parameter
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      loco_lead_form_vending: params.get('loco_lead_form_vending')
    }
  }, [location])

  // Override the feature flags based on the query parameters
  useEffect(() => {
    if(Object.keys(queryParams).length === 0) {
      return
    }

    const overrideExperiments = Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value === 'true') {
        const variant = shownVariant(experiments[key], value)
        posthog.featureFlags.overrideFeatureFlags({ [key]: variant })
        return { ...acc, [key]: variant }
      }
      return acc
    }, experiments)

    if(Object.keys(overrideExperiments).length > 0) {
      setExperiments(overrideExperiments)
      reloadExperiments()
    }
  }, [queryParams])
  

  const reloadExperiments = () => {
    if(!posthog.featureFlags.hasLoadedFlags) {
      return
    }

    posthog.reloadFeatureFlags()

    posthog.onFeatureFlags(() => {
        // Get current feature flags from PostHog and update local state
      const currentFlags = posthog.featureFlags.getFlagsWithDetails()
      
      const updatedExperiments = {
        ...experiments,
        ...Object.entries(currentFlags).reduce((acc, [key, value]) => {
          if (key.startsWith('loco_lead_form_')) {
            // value will be the variant (e.g. 'test' or 'control')
            return { ...acc, [key]: value.variant }
          }
          return acc
        }, {})
      }
      
      setExperiments(updatedExperiments)
    })

  }
  
  if (!ref.current && props.isOpen) {
    ref.current = true
  }

  if (ref.current === undefined) {
    return null
  }

  return (
    <LeadFormModal
      {...props}
      experiments={experiments}
      reloadExperiments={reloadExperiments}
      posthog={posthog}
      recording={recording}
      initialData={initialData}
      getInitialData={getInitialData}
      data={data}
      setData={setData}
      sessionData={sessionData}
      setSessionData={setSessionData}
    />
  )
}
